import c from "classnames";
import moment from "jalali-moment";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { useTranslation } from "next-i18next";
import React, { useEffect, useRef } from "react";

import api from "helpers/api";
import Res from "types/response";
import { setUser } from "store/user";
import { User, Gender, User$ } from "types/user";
import { DatePicker } from "./filters/date-picker";
import Feedback from "components/feedback/feedback";

interface Props { user: User$ }
export function ProfileForm({ user }: Props): JSX.Element {
  const router = useRouter();
  const { t } = useTranslation();
  const { register, handleSubmit, formState,
    watch, setValue, reset } = useForm();
  const { isDirty, isValid, errors } = formState;

  // const birthDate = register("birth_date");
  const datePicker = useRef<DatePicker>(null);

  const genderInput = register('gender');
  const gender = watch('gender', user?.gender || 0);

  useEffect(() => {
    if (!user) { return; }
    const { first_name, last_name, birth_date, gender } = user;
    reset({ first_name, last_name, birth_date, gender });
    if (!birth_date) { return; }
    datePicker.current?.setValue([moment(birth_date)], false);
  }, [user]);

  const submitBtn = useRef<HTMLButtonElement>(null);
  async function submit(form: User): Promise<void> {
    const { data }: Res<User> = await api.set
      ('v1/users/profile', 'put', { body: form, ref: submitBtn });
    setUser({ ...user, ...data });
  };

  useEffect(() => () => api.remove('put:v1/users/profile'), []);

  return (
    <form className="row px-3 px-lg-0 pt-lg-5" onSubmit={handleSubmit(submit as any)}>
      <div className="col-lg-6">
        <div className={c("userName mb-3 mb-lg-0", { 'has-error': errors.first_name })}>
          <input type="search" {...register("first_name", { required: true })} />
          <label htmlFor="">{t("common:form.name")}</label>
          {/* <span className="clearfield">
            <span className="icon icon-close"></span>
          </span> */}
          <Feedback {...{ formState }} field="first_name"></Feedback>
        </div>
      </div>
      <div className="col-lg-6">
        <div className={c("userName mb-3 mb-lg-0", { 'has-error': errors.last_name })}>
          <input type="search" {...register("last_name", { required: true })} />
          <label htmlFor="">{t("common:form.family")}</label>
          {/* <span className="clearfield">
            <span className="icon icon-close"></span>
          </span> */}
          <Feedback {...{ formState }} field="last_name"></Feedback>
        </div>
      </div>
      <div className="col-lg-6">
        <DatePicker {...{ t, router }} placeholder="0000/00/00"
          className="form-control DateofBirth mt-4 mb-3 mb-lg-0"
          ref={datePicker} maxDate={moment()} years={[99, 0]}
          onChange={([d]) => setValue('birth_date',
            d?.format('YYYY-MM-DD'), { shouldDirty: true })}>
        </DatePicker>
      </div>
      <div className="col-lg-6">
        <div className="selectGender mt-4 mb-3 mb-lg-0 pointer" tabIndex={0}>
          <div className="d-flex align-items-center justify-content-between">
            <span className={c(`${gender > 0 && "text-color"}`)}>
              <span className={`icon icon-${Gender[gender]} ms-n2 pe-3`}></span>
              {t(`common:form.gender.${Gender[gender]}`)}
            </span>
            <span className="d-inline-block icon icon-right-chevron"></span>
          </div>
          <div className="options" onClick={e => e.currentTarget.parentElement?.blur()}>
          {[1, 2, 3].map((gender) => (
            <div className="option" key={gender}
              onClick={() => setValue('gender', gender, { shouldDirty: true })}>
              <span className={`icon icon-${Gender[gender]} pe-3`}></span>
              <span>{t(`common:form.gender.${Gender[gender]}`)}</span>
            </div>
          ))}
          </div>
        </div>
      </div>
      <div className="col-lg-auto my-4">
        <button className="btn btn-primary w-100"
          disabled={!isValid || !isDirty} ref={submitBtn}>
          {t("common:user.update")}
        </button>
      </div>
    </form>
  )
}

export default React.memo(ProfileForm);
